import { Badge } from '@nautilusbio/nb-design-system/Badge';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { Check, Copy } from 'lucide-react';
import React, { type FC, memo } from 'react';

import styles from './styles.module.css';
import type { CurrentOrgSectionProps } from './types';
import { formatRoleLabel } from './utils';

/**
 * Displays the currently selected org with a demoted copy-ID button
 * and an inline role switcher for the current org.
 */
export const CurrentOrgSection: FC<CurrentOrgSectionProps> = memo(
  ({ selectedOrg, allowedRoles, currentRole, onRoleSwitch, onCopyOrgId, isPending, tOrgs }) => {
    return (
      <Stack gap={4} orientation="vertical">
        {/* Header: org name + copy-ID */}
        <Stack alignItems="center" justifyContent="space-between" orientation="horizontal">
          <Text as="h4">{selectedOrg.name}</Text>

          <Tooltip
            content={tOrgs('org_selector_copy_tooltip')}
            placement="left"
            trigger={
              <Button data-testid="copy-org-id-btn" onPress={onCopyOrgId} size="xs" visual="unstyled">
                <Copy size={12} />
              </Button>
            }
          />
        </Stack>

        {/* Multiple roles: horizontal toggle */}
        {allowedRoles.length > 1 && (
          <Stack fullWidth gap={2} justifyContent="space-evenly" orientation="horizontal">
            {allowedRoles.map(role => {
              const isActive = role === currentRole;

              return (
                <Button
                  className={styles.currentRoleOrgSelector}
                  data-testid={`role-selector-${role}`}
                  key={role}
                  isDisabled={isActive || isPending}
                  onPress={() => onRoleSwitch(selectedOrg, role)}
                  size="xs"
                  visual={isActive ? 'success' : 'outline'}
                >
                  <Stack alignItems="center" as="span" gap={2} orientation="horizontal">
                    {formatRoleLabel(role)}

                    {isActive && <Check size={10} />}
                  </Stack>
                </Button>
              );
            })}
          </Stack>
        )}

        {/* Single role: static badge */}
        {allowedRoles.length === 1 && currentRole && <Badge variant="success">{formatRoleLabel(currentRole)}</Badge>}

        {/* No roles: warning */}
        {allowedRoles.length === 0 && <Text as="span">{tOrgs('org_selector_no_roles')}</Text>}
      </Stack>
    );
  },
);

CurrentOrgSection.displayName = 'CurrentOrgSection';
